/**
 * Live PHM store + background sync.
 * React state is lost when you leave /productive-hours; this module keeps the latest
 * snapshot and refreshes it every 15s for any logged-in user with phm.access.
 */

import api from '../../utils/api';
import phmApi from '../../utils/phmApi';

export const PHM_LIVE_API = '/api/productive-hours';
export const PHM_LIVE_POLL_MS = 15000;

export const PHM_DEFAULT_LIVE_SCOPE = {
  shiftFilters: [],
  teamFilters: [],
  microTeamFilters: []
};

export const phmLiveScopeKey = (scope) => {
  const shift = scope?.shiftFilter ?? scope?.shiftFilters?.[0] ?? '';
  const team = scope?.teamFilter ?? scope?.teamFilters?.[0] ?? '';
  const micro = scope?.microTeamFilters ?? [];
  return JSON.stringify({
    shift: String(shift || ''),
    team: String(team || ''),
    micro: [...micro].map(String).sort()
  });
};

export const PHM_DEFAULT_LIVE_SCOPE_KEY = phmLiveScopeKey(PHM_DEFAULT_LIVE_SCOPE);

export const buildPhmLiveScopeParams = (scope) => {
  const params = new URLSearchParams();
  const shiftFilters = scope?.shiftFilters ?? (scope?.shiftFilter ? [scope.shiftFilter] : []);
  const teamFilters = scope?.teamFilters ?? (scope?.teamFilter ? [scope.teamFilter] : []);
  const microTeamFilters = scope?.microTeamFilters ?? [];
  if (shiftFilters.length) params.set('shift', shiftFilters.join(','));
  if (teamFilters.length) params.set('team', teamFilters.join(','));
  if (microTeamFilters.length) params.set('microTeam', microTeamFilters.join(','));
  return params;
};

const emptySnapshot = () => ({
  context: null,
  summary: null,
  summaryScopeKey: null,
  rows: [],
  rowsScopeKey: null,
  statusLabels: {},
  lastUpdatedAt: ''
});

let store = emptySnapshot();
const listeners = new Set();
let refreshInFlight = false;
let pollTimer = null;
let backgroundRetainCount = 0;
let contextLoaded = false;

const notify = () => {
  listeners.forEach((fn) => {
    try {
      fn();
    } catch (_) {
      /* listener */
    }
  });
};

export const phmLiveSessionCache = {
  getSnapshot() {
    return {
      ...store,
      rows: [...store.rows],
      statusLabels: { ...store.statusLabels }
    };
  },

  subscribe(listener) {
    listeners.add(listener);
    return () => listeners.delete(listener);
  },

  isRefreshing() {
    return refreshInFlight;
  },

  hasDashboard(scopeKey) {
    const summaryOk = store.summary && store.summaryScopeKey === scopeKey;
    const rowsOk = store.rows.length > 0 && store.rowsScopeKey === scopeKey;
    return Boolean(summaryOk || rowsOk);
  },

  readForScope(scopeKey) {
    return {
      context: store.context,
      summary: store.summaryScopeKey === scopeKey ? store.summary : null,
      rows: store.rowsScopeKey === scopeKey ? [...store.rows] : [],
      statusLabels: store.rowsScopeKey === scopeKey ? { ...store.statusLabels } : {},
      lastUpdatedAt: store.lastUpdatedAt
    };
  },

  writeContext(context) {
    if (context) store.context = context;
  },

  writeResults({ rows, statusLabels, scopeKey, lastUpdatedAt }) {
    store.rows = Array.isArray(rows) ? rows : [];
    store.statusLabels = statusLabels && typeof statusLabels === 'object' ? { ...statusLabels } : {};
    store.rowsScopeKey = scopeKey;
    if (lastUpdatedAt) store.lastUpdatedAt = lastUpdatedAt;
    notify();
  },

  writeSummary(summary, scopeKey) {
    store.summary = summary;
    store.summaryScopeKey = scopeKey;
    if (summary?.liveContext) store.context = summary.liveContext;
    notify();
  },

  clear() {
    store = emptySnapshot();
    contextLoaded = false;
    notify();
  }
};

const loadLiveContextOnce = () => {
  if (contextLoaded) return;
  contextLoaded = true;
  void api.get(`${PHM_LIVE_API}/live-context`).then((r) => {
    phmLiveSessionCache.writeContext(r.data || null);
    notify();
  }).catch(() => {
    contextLoaded = false;
  });
};

/** Fetch live-results + dashboard-summary for a scope and update the store. */
export const refreshPhmLiveStore = async (scope = PHM_DEFAULT_LIVE_SCOPE, options = {}) => {
  const { noCache = false } = options;
  if (refreshInFlight) return;
  refreshInFlight = true;
  notify();
  const scopeKey = phmLiveScopeKey(scope);
  try {
    loadLiveContextOnce();
    const params = buildPhmLiveScopeParams(scope);
    if (noCache) params.set('noCache', '1');
    const suffix = params.toString() ? `?${params.toString()}` : '';
    const [resultsRes, summaryRes] = await Promise.allSettled([
      phmApi.get(`${PHM_LIVE_API}/live-results${suffix}`),
      phmApi.get(`${PHM_LIVE_API}/live/dashboard-summary${suffix}`)
    ]);
    if (resultsRes.status === 'fulfilled') {
      const data = resultsRes.value.data || {};
      if (data.context) phmLiveSessionCache.writeContext(data.context);
      phmLiveSessionCache.writeResults({
        rows: data.rows || [],
        statusLabels: data.statusLabels || {},
        scopeKey,
        lastUpdatedAt: new Date().toLocaleTimeString()
      });
    }
    if (summaryRes.status === 'fulfilled') {
      const summary = summaryRes.value.data || null;
      if (summary?.liveContext) phmLiveSessionCache.writeContext(summary.liveContext);
      if (summary) phmLiveSessionCache.writeSummary(summary, scopeKey);
    }
  } catch (_) {
    /* best-effort; page/manual refresh can retry */
  } finally {
    refreshInFlight = false;
    notify();
  }
};

const startBackgroundPoll = () => {
  if (pollTimer) return;
  void refreshPhmLiveStore(PHM_DEFAULT_LIVE_SCOPE);
  pollTimer = window.setInterval(() => {
    if (document.visibilityState !== 'visible') return;
    void refreshPhmLiveStore(PHM_DEFAULT_LIVE_SCOPE);
  }, PHM_LIVE_POLL_MS);
};

const stopBackgroundPoll = () => {
  if (pollTimer) {
    window.clearInterval(pollTimer);
    pollTimer = null;
  }
};

/** Start 15s live sync while retained (e.g. on /productive-hours only). */
export const retainPhmLiveBackgroundSync = () => {
  backgroundRetainCount += 1;
  if (backgroundRetainCount === 1) startBackgroundPoll();
};

export const releasePhmLiveBackgroundSync = () => {
  backgroundRetainCount = Math.max(0, backgroundRetainCount - 1);
  if (backgroundRetainCount === 0) stopBackgroundPoll();
};
